import { useState, useRef, ChangeEvent } from 'react'
import { FIELD_LIMITS, truncateField, validateAuthors, validateCategories } from '../../utils/validation'
import { useModalBehavior } from '../../hooks/useModalBehavior'
import TagInput from '../common/TagInput'
import styles from './BulkEditModal.module.css'
import {
    FORMAT_OPTIONS,
    STATUS_OPTIONS,
    PROGRESS_OPTIONS,
    getFormatLabel,
    getStatusLabel,
    getProgressLabel,
} from '@/constants/bookOptions'



type EnabledFields = Record<string, boolean>

interface BulkUpdates {
    author?: string[]
    publisher?: string | null
    series?: string | null
    location?: string | null
    category?: string[]
    language?: string | null
    status?: string
    progress?: string
    notes?: string
    format?: string
    synopsis?: string | null
    description?: string | null
    page_count?: number
}

interface BulkEditModalProps {
    selectedCount: number
    onClose: () => void
    onApply: (updates: BulkUpdates) => void
    existingAuthors?: string[]
    existingPublishers?: string[]
    existingLocations?: string[]
    existingSeries?: string[]
    existingCategories?: string[]
    existingLanguages?: string[]
}

export default function BulkEditModal({
    selectedCount,
    onClose,
    onApply,
    existingAuthors = [],
    existingPublishers = [],
    existingLocations = [],
    existingSeries = [],
    existingCategories = [],
    existingLanguages = [],
}: Readonly<BulkEditModalProps>) {
    const [enabledFields, setEnabledFields] = useState<EnabledFields>({
        author: false,
        publisher: false,
        series: false,
        location: false,
        category: false,
        language: false,
        status: false,
        progress: false,
        notes: false,
        format: false,
        synopsis: false,
        description: false,
        page_count: false,
    })

    const [values, setValues] = useState({
        author: [] as string[],
        publisher: '',
        series: '',
        location: '',
        category: [] as string[],
        language: '',
        status: 'owned',
        progress: 'not_read',
        notes: '',
        format: 'physical',
        synopsis: '',
        description: '',
        page_count: '',
    })

    const [showPublisherSuggestions, setShowPublisherSuggestions] = useState(false)
    const [showLocationSuggestions, setShowLocationSuggestions] = useState(false)
    const [showSeriesSuggestions, setShowSeriesSuggestions] = useState(false)
    const [showLanguageSuggestions, setShowLanguageSuggestions] = useState(false)

    const modalRef = useRef<HTMLDivElement>(null)
    const publisherInputRef = useRef<HTMLDivElement>(null)
    const locationInputRef = useRef<HTMLDivElement>(null)
    const seriesInputRef = useRef<HTMLDivElement>(null)
    const languageInputRef = useRef<HTMLDivElement>(null)

    useModalBehavior({
        modalRef,
        onClose,
        suggestionRefs: [
            { ref: publisherInputRef, setter: setShowPublisherSuggestions },
            { ref: locationInputRef, setter: setShowLocationSuggestions },
            { ref: seriesInputRef, setter: setShowSeriesSuggestions },
            { ref: languageInputRef, setter: setShowLanguageSuggestions },
        ],
    })

    const toggleField = (field: string) => {
        setEnabledFields((prev) => {
            const isEnabled = Object.hasOwn(prev, field) ? prev[field] : false
            return { ...prev, [field]: !isEnabled }
        })
    }

    const updateValue = (field: string, value: BulkUpdates[keyof BulkUpdates]) => {
        setValues((prev) => ({ ...prev, [field]: value }))
    }

    const handlePageCountChange = (value: string) => {
        if (value === '' || /^\d+$/.test(value)) {
            updateValue('page_count', value)
        }
    }

    const handleApply = () => {
        const b = FIELD_LIMITS.books
        const updates: BulkUpdates = {}
        if (enabledFields.author) updates.author = validateAuthors(values.author)
        if (enabledFields.publisher) updates.publisher = truncateField(values.publisher.trim(), b.publisher) || null
        if (enabledFields.series) updates.series = truncateField(values.series.trim(), b.series) || null
        if (enabledFields.location) updates.location = truncateField(values.location.trim(), b.location) || null
        if (enabledFields.category) updates.category = validateCategories(values.category)
        if (enabledFields.language) updates.language = truncateField(values.language.trim(), b.language) || null
        if (enabledFields.status) updates.status = values.status
        if (enabledFields.progress) updates.progress = values.progress
        if (enabledFields.notes) updates.notes = truncateField(values.notes, b.notes)
        if (enabledFields.format) updates.format = values.format
        if (enabledFields.synopsis) updates.synopsis = truncateField(values.synopsis.trim(), b.synopsis) || null
        if (enabledFields.description) updates.description = truncateField(values.description.trim(), b.description) || null
        if (enabledFields.page_count && values.page_count) updates.page_count = Number.parseInt(values.page_count, 10)

        if (Object.keys(updates).length > 0) {
            onApply(updates)
        }
        onClose()
    }

    const hasEnabledFields = Object.values(enabledFields).some(Boolean)

    const filteredPublishers = existingPublishers.filter(
        (e) => e && e.toLowerCase().includes(values.publisher.toLowerCase()) && e !== values.publisher
    )
    const filteredLocations = existingLocations.filter(
        (u) => u && u.toLowerCase().includes(values.location.toLowerCase()) && u !== values.location
    )
    const filteredSeries = existingSeries.filter((s) => s && s.toLowerCase().includes(values.series.toLowerCase()) && s !== values.series)
    const filteredLanguages = existingLanguages.filter((i) => i && i.toLowerCase().includes(values.language.toLowerCase()) && i !== values.language)

    return (
        <div className={styles.bulkEditOverlay}>
            <div className={styles.bulkEditModal} ref={modalRef}>
                <div className={styles.bulkEditHeader}>
                    <h3 className={styles.bulkEditTitle}>
                        <span>✏️</span>
                        Edición Masiva
                    </h3>
                    <span className={styles.bulkEditCount}>{selectedCount} libros</span>
                    <button className={styles.bulkEditClose} onClick={onClose} aria-label="Cerrar">
                        ✕
                    </button>
                </div>

                <div className={styles.bulkEditBody}>
                    <p className={styles.bulkEditHint}>Marca los campos que deseas modificar. Los campos no marcados mantendrán sus valores actuales.</p>

                    {/* Author */}
                    <div className={`${styles.bulkEditField} ${enabledFields.author ? styles.enabled : ''}`}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.author} onChange={() => toggleField('author')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>✍️ Autor</span>
                        </label>
                        {enabledFields.author && (
                            <TagInput
                                value={values.author}
                                onChange={(newAuthors) => updateValue('author', newAuthors)}
                                suggestions={existingAuthors}
                                placeholder="Añadir autores..."
                                maxTags={FIELD_LIMITS.books.author_max_items}
                                maxLength={FIELD_LIMITS.books.author_max_length}
                            />
                        )}
                    </div>

                    {/* Publisher */}
                    <div className={`${styles.bulkEditField} ${enabledFields.publisher ? styles.enabled : ''}`} ref={publisherInputRef}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.publisher} onChange={() => toggleField('publisher')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>🏢 Editorial</span>
                        </label>
                        {enabledFields.publisher && (
                            <div className={styles.hybridWrapper}>
                                <input
                                    type="text"
                                    className={styles.bulkEditInput}
                                    value={values.publisher}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue('publisher', truncateField(e.target.value, FIELD_LIMITS.books.publisher))}
                                    onFocus={() => setShowPublisherSuggestions(true)}
                                    placeholder="Nombre de la editorial"
                                    maxLength={FIELD_LIMITS.books.publisher}
                                />
                                {showPublisherSuggestions && filteredPublishers.length > 0 && (
                                    <div className={styles.suggestionsDropdown}>
                                        {filteredPublishers.slice(0, 5).map((publisher, i) => (
                                            <button
                                                key={i}
                                                className={styles.suggestionItem}
                                                onClick={() => {
                                                    updateValue('publisher', publisher)
                                                    setShowPublisherSuggestions(false)
                                                }}
                                            >
                                                {publisher}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Series */}
                    <div className={`${styles.bulkEditField} ${enabledFields.series ? styles.enabled : ''}`} ref={seriesInputRef}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.series} onChange={() => toggleField('series')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>📚 Serie</span>
                        </label>
                        {enabledFields.series && (
                            <div className={styles.hybridWrapper}>
                                <input
                                    type="text"
                                    className={styles.bulkEditInput}
                                    value={values.series}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue('series', truncateField(e.target.value, FIELD_LIMITS.books.series))}
                                    onFocus={() => setShowSeriesSuggestions(true)}
                                    placeholder="Nombre de la series"
                                    maxLength={FIELD_LIMITS.books.series}
                                />
                                {showSeriesSuggestions && filteredSeries.length > 0 && (
                                    <div className={styles.suggestionsDropdown}>
                                        {filteredSeries.slice(0, 5).map((seriesName, i) => (
                                            <button
                                                key={i}
                                                className={styles.suggestionItem}
                                                onClick={() => {
                                                    updateValue('series', seriesName)
                                                    setShowSeriesSuggestions(false)
                                                }}
                                            >
                                                {seriesName}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Location */}
                    <div className={`${styles.bulkEditField} ${enabledFields.location ? styles.enabled : ''}`} ref={locationInputRef}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.location} onChange={() => toggleField('location')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>📍 Ubicación</span>
                        </label>
                        {enabledFields.location && (
                            <div className={styles.hybridWrapper}>
                                <input
                                    type="text"
                                    className={styles.bulkEditInput}
                                    value={values.location}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue('location', truncateField(e.target.value, FIELD_LIMITS.books.location))}
                                    onFocus={() => setShowLocationSuggestions(true)}
                                    placeholder="Location"
                                    maxLength={FIELD_LIMITS.books.location}
                                />
                                {showLocationSuggestions && filteredLocations.length > 0 && (
                                    <div className={styles.suggestionsDropdown}>
                                        {filteredLocations.slice(0, 5).map((loc, i) => (
                                            <button
                                                key={i}
                                                className={styles.suggestionItem}
                                                onClick={() => {
                                                    updateValue('location', loc)
                                                    setShowLocationSuggestions(false)
                                                }}
                                            >
                                                {loc}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Categoría */}
                    <div className={`${styles.bulkEditField} ${enabledFields.category ? styles.enabled : ''}`}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.category} onChange={() => toggleField('category')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>🏷️ Categoría</span>
                        </label>
                        {enabledFields.category && (
                            <TagInput
                                value={values.category}
                                onChange={(newCategories) => updateValue('category', newCategories)}
                                suggestions={existingCategories}
                                placeholder="Añadir categorías..."
                                maxTags={FIELD_LIMITS.books.category_max_items}
                                maxLength={FIELD_LIMITS.books.category}
                            />
                        )}
                    </div>

                    {/* Language */}
                    <div className={`${styles.bulkEditField} ${enabledFields.language ? styles.enabled : ''}`} ref={languageInputRef}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.language} onChange={() => toggleField('language')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>🌐 Idioma</span>
                        </label>
                        {enabledFields.language && (
                            <div className={styles.hybridWrapper}>
                                <input
                                    type="text"
                                    className={styles.bulkEditInput}
                                    value={values.language}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => updateValue('language', truncateField(e.target.value, FIELD_LIMITS.books.language))}
                                    onFocus={() => setShowLanguageSuggestions(true)}
                                    placeholder="Idioma"
                                    maxLength={FIELD_LIMITS.books.language}
                                />
                                {showLanguageSuggestions && filteredLanguages.length > 0 && (
                                    <div className={styles.suggestionsDropdown}>
                                        {filteredLanguages.slice(0, 5).map((lang, i) => (
                                            <button
                                                key={i}
                                                className={styles.suggestionItem}
                                                onClick={() => {
                                                    updateValue('language', lang)
                                                    setShowLanguageSuggestions(false)
                                                }}
                                            >
                                                {lang}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Estado, Progreso, Formato - selects */}
                    <div className={`${styles.bulkEditField} ${enabledFields.status ? styles.enabled : ''}`}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.status} onChange={() => toggleField('status')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>🏷️ Estado</span>
                        </label>
                        {enabledFields.status && (
                            <select className={styles.bulkEditSelect} value={values.status} onChange={(e: ChangeEvent<HTMLSelectElement>) => updateValue('status', e.target.value)}>
                                {STATUS_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                        {getStatusLabel(opt)}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div className={`${styles.bulkEditField} ${enabledFields.progress ? styles.enabled : ''}`}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.progress} onChange={() => toggleField('progress')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>📊 Progreso</span>
                        </label>
                        {enabledFields.progress && (
                            <select className={styles.bulkEditSelect} value={values.progress} onChange={(e: ChangeEvent<HTMLSelectElement>) => updateValue('progress', e.target.value)}>
                                {PROGRESS_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                        {getProgressLabel(opt)}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    <div className={`${styles.bulkEditField} ${enabledFields.format ? styles.enabled : ''}`}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.format} onChange={() => toggleField('format')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>📦 Formato</span>
                        </label>
                        {enabledFields.format && (
                            <select className={styles.bulkEditSelect} value={values.format} onChange={(e: ChangeEvent<HTMLSelectElement>) => updateValue('format', e.target.value)}>
                                {FORMAT_OPTIONS.map((opt) => (
                                    <option key={opt} value={opt}>
                                        {getFormatLabel(opt)}
                                    </option>
                                ))}
                            </select>
                        )}
                    </div>

                    {/* Textareas */}
                    <div className={`${styles.bulkEditField} ${enabledFields.notes ? styles.enabled : ''}`}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.notes} onChange={() => toggleField('notes')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>📝 Notas</span>
                        </label>
                        {enabledFields.notes && (
                            <textarea
                                className={styles.bulkEditTextarea}
                                value={values.notes}
                                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateValue('notes', truncateField(e.target.value, FIELD_LIMITS.books.notes))}
                                placeholder="Notas (se aplicarán a todos)"
                                maxLength={FIELD_LIMITS.books.notes}
                                rows={2}
                            />
                        )}
                    </div>

                    <div className={`${styles.bulkEditField} ${enabledFields.synopsis ? styles.enabled : ''}`}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.synopsis} onChange={() => toggleField('synopsis')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>📄 Sinopsis</span>
                        </label>
                        {enabledFields.synopsis && (
                            <>
                                <textarea
                                    className={styles.bulkEditTextarea}
                                    value={values.synopsis}
                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateValue('synopsis', truncateField(e.target.value, FIELD_LIMITS.books.synopsis))}
                                    placeholder="Sinopsis (se aplicará a todos)"
                                    maxLength={FIELD_LIMITS.books.synopsis}
                                    rows={3}
                                />
                                <span className={`${styles.charCounter} ${values.synopsis.length >= FIELD_LIMITS.books.synopsis * 0.9 ? styles.warning : ''}`}>
                                    {values.synopsis.length}/{FIELD_LIMITS.books.synopsis}
                                </span>
                            </>
                        )}
                    </div>

                    <div className={`${styles.bulkEditField} ${enabledFields.description ? styles.enabled : ''}`}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.description} onChange={() => toggleField('description')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>📋 Descripción</span>
                        </label>
                        {enabledFields.description && (
                            <>
                                <textarea
                                    className={styles.bulkEditTextarea}
                                    value={values.description}
                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => updateValue('description', truncateField(e.target.value, FIELD_LIMITS.books.description))}
                                    placeholder="Descripción (se aplicará a todos)"
                                    maxLength={FIELD_LIMITS.books.description}
                                    rows={3}
                                />
                                <span className={`${styles.charCounter} ${values.description.length >= FIELD_LIMITS.books.description * 0.9 ? styles.warning : ''}`}>
                                    {values.description.length}/{FIELD_LIMITS.books.description}
                                </span>
                            </>
                        )}
                    </div>

                    <div className={`${styles.bulkEditField} ${enabledFields.page_count ? styles.enabled : ''}`}>
                        <label className={styles.bulkEditCheckbox}>
                            <input type="checkbox" checked={enabledFields.page_count} onChange={() => toggleField('page_count')} />
                            <span className={styles.checkmark}></span>
                            <span className={styles.fieldLabel}>📖 Número de Páginas</span>
                        </label>
                        {enabledFields.page_count && (
                            <input
                                type="text"
                                inputMode="numeric"
                                className={styles.bulkEditInput}
                                value={values.page_count}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => handlePageCountChange(e.target.value)}
                                placeholder="Ej: 350"
                            />
                        )}
                    </div>
                </div>

                <div className={styles.bulkEditFooter}>
                    <button className="btn-secondary" onClick={onClose}>
                        Cancelar
                    </button>
                    <button className="btn-primary" onClick={handleApply} disabled={!hasEnabledFields}>
                        Aplicar a {selectedCount} libros
                    </button>
                </div>
            </div>
        </div>
    )
}
